import React, { useEffect, useState } from 'react';
import { X, Search, FileText, Loader2 } from 'lucide-react';
import { documentService } from '../services/documentService';
import { useLanguage } from '../i18n';
import Modal from './ui/Modal';

/**
 * Shared "Select from File Manager" picker — a searchable list of the user's
 * existing latest-version documents. Selecting one attaches it WITHOUT
 * re-uploading (the caller builds the attachment from the doc row).
 *
 * Extracted from BoardsPanel (#117, Document attachment Boards) so the chat
 * composer's "+" menu (#208) can reuse the exact same pattern. `onSelect` is
 * called as `onSelect(doc.id, doc)` — the id is passed first for back-compat
 * with the boards caller (which only needs the id), and the full doc row is
 * passed second so the chat caller can build a completed attachment descriptor
 * (filename / mime_type / file_size) without a second fetch.
 *
 * `testIds` overrides the default (boards) data-testids so a second instance on
 * the same page (chat) can be targeted independently in E2E.
 */
const AttachPickerModal = ({ onClose, onSelect, title, testIds = {} }) => {
    const { t } = useLanguage();
    // Resolved here (not as a prop default) so the default goes through t() —
    // reuses the existing `attachFromFileManager` key (Notes & Reminders'
    // paperclip menu already carries that exact English string). A caller MAY
    // still pass an explicit `title` override.
    const resolvedTitle = title ?? t('attachFromFileManager', 'Select from File Manager');
    const ids = {
        modal: testIds.modal || 'attach-picker-modal',
        search: testIds.search || 'attach-picker-search',
        item: testIds.item || 'attach-picker-item',
    };
    const [docs, setDocs] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await documentService.listDocuments({ per_page: 200 });
                if (!cancelled) setDocs(r.documents || []);
            } catch (_) { /* show empty */ }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, []);

    const q = search.trim().toLowerCase();
    const filtered = q
        ? docs.filter((d) => (d.original_filename || d.filename || '').toLowerCase().includes(q))
        : docs;

    const pick = async (doc) => {
        setBusyId(doc.id);
        try { await onSelect(doc.id, doc); } finally { setBusyId(null); }
    };

    return (
        // Escape closes; a backdrop click deliberately does not (never did).
        <Modal onClose={onClose} zIndex="z-[60]" layout="column" closeOnBackdrop={false} cardClassName="max-h-[80dvh]" scrimTestId={ids.modal}>
                <div className="flex items-center justify-between p-4 border-b border-pai-subtle">
                    <h2 className="text-base font-semibold text-white">{resolvedTitle}</h2>
                    <button onClick={onClose} aria-label={t('commonClose', 'Close')} className="text-pai-meta hover:text-white "><X size={18} /></button>
                </div>
                <div className="p-3 border-b border-pai-subtle">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-pai-faint pointer-events-none" />
                        <input
                            data-testid={ids.search}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('fmSearchDocumentsEllipsis', 'Search documents…')}
                            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-white/[0.05] border border-transparent focus:border-white/80 text-white focus:outline-none"
                        />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                    {loading && <div className="p-4 text-sm text-pai-meta">{t('fmLoadingEllipsisAlt', 'Loading…')}</div>}
                    {!loading && filtered.length === 0 && <div className="p-4 text-sm text-pai-meta">{t('fmNoDocumentsFoundPeriod', 'No documents found.')}</div>}
                    {filtered.map((d) => (
                        <button
                            key={d.id}
                            type="button"
                            data-testid={ids.item}
                            onClick={() => pick(d)}
                            disabled={busyId === d.id}
                            className="w-full flex items-center gap-2 p-2 rounded text-left text-sm hover:bg-white/[0.07] disabled:opacity-50"
                        >
                            <FileText size={15} className="text-pai-faint flex-shrink-0" />
                            <span className="flex-1 min-w-0 truncate text-white" title={d.original_filename || d.filename}>{d.original_filename || d.filename}</span>
                            {busyId === d.id && <Loader2 size={13} className="animate-spin text-pai-faint" />}
                        </button>
                    ))}
                </div>
            </Modal>
    );
};

export default AttachPickerModal;
